import { useCallback, useEffect, useState } from "react";
import { getLifetimePerformance } from "../../../services/clearing/station-performance";
import type { LifetimeReport, ReportingOptions } from "../../../types/stationPerformance";

interface UseLifetimePerformanceArgs {
  asOf: string;
  options: ReportingOptions;
  sort?: string;
  order?: "asc" | "desc";
  includeNeverActive?: boolean;
}

export const useLifetimePerformance = ({
  asOf,
  options,
  sort = "lifetime.moneyCents",
  order = "desc",
  includeNeverActive = false,
}: UseLifetimePerformanceArgs) => {
  const [report, setReport] = useState<LifetimeReport | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [neverComputed, setNeverComputed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    if (!asOf) return;
    setNeverComputed(null);
    setError(null);
    const result = await getLifetimePerformance({
      asOf,
      options,
      sort,
      order,
      includeNeverActive,
      limit: 200,
    });
    if (result?.success) {
      setReport(result.data ?? null);
    } else if (result?.neverComputed) {
      setNeverComputed(result.months ?? [asOf]);
      setReport(null);
    } else {
      setError(result?.message ?? "Could not load the lifetime league table.");
    }
    setInitialLoading(false);
  }, [asOf, options, sort, order, includeNeverActive]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { report, initialLoading, neverComputed, error, fetchReport };
};
